import React, { createRef, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import bp from 'styled-components-breakpoint';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
import MapPinFilled from '../Icons/SVG/MapPinFilled';
import ChevronDown from '../Icons/SVG/ChevronDown';
import Check from '../Icons/SVG/Check';
import theme from '../../themes';

const RegionButton = styled.button`
  ${(props) => props.theme.typography.buttonSmall};
  align-items: center;
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.grayscale.white};
  cursor: pointer;
  display: flex;
  height: 100%;
  padding: 0 0.5rem;
  position: relative;

  svg {
    display: block;

    path {
      fill: ${(props) =>
        props['data-isopen']
          ? props.theme.colors.grayscale.white
          : props.theme.colors.grayscale.white};
    }
  }

  &:hover {
    color: ${(props) => props.theme.colors.grayscale.black};

    svg {
      display: block;

      path:last-child {
        fill: ${(props) => props.theme.colors.grayscale.black};
      }
    }
  }
`;

const RegionText = styled.div`
  display: flex;
  padding: 0 0.5rem;

  span {
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const StyledRegionContainer = styled.div`
  align-self: center;
  display: inline-block;
  height: 2rem;
  position: relative;

  &:focus:enabled {
    outline: 0;
  }

  &[aria-expanded='true'] {
    ${RegionButton} {
      background-color: #53565a;
      color: black;

      &:focus {
        outline: 0;
      }

      &::before {
        ${(props) => props.theme.borders.borderBlack1};
        bottom: 0;
        content: '';
        display: block;
        left: 0;
        position: absolute;
        right: 0;
      }

      svg {
        path {
          fill: ${(props) => props.theme.colors.grayscale.black};
        }
      }
    }
  }
`;

const StyledMenu = styled.ul`
  background-color: ${(props) => props.theme.colors.grayscale.white};
  border-radius: 0;
  box-shadow:
    0 12px 17px 2px rgb(0 0 0 / 14%),
    0 5px 22px 4px rgb(0 0 0 / 12%),
    0 7px 8px -4px rgb(0 0 0 / 20%);
  display: flex;
  flex-flow: column wrap;
  list-style: none;
  margin: 0;
  max-height: 29rem;
  padding: 0.25rem 0 1rem;
  position: absolute;
  right: 0;
  top: 105%;
  visibility: ${(props) => (props.isOpen ? 'visible' : 'hidden')};
  width: 41.25rem;
  z-index: ${(props) => props.theme.zIndex.drawer};
  ${bp('desktop')`
    right: 0;
  `}

  h4 {
    ${(props) => props.theme.typography.subHeader};
    color: ${(props) => props.theme.colors.grayscale.gray0};
    margin: 0;
    padding: 0.5rem 0 0.5rem 1rem;
  }
`;

const StyledListEntry = styled.li`
  button {
    ${(props) => props.theme.typography.body};
    background: none;
    border: none;
    color: ${(props) => props.theme.colors.grayscale.black};
    cursor: pointer;
    min-width: 13rem;
    overflow: hidden;
    padding: 0.5rem 0;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;

    span:first-child {
      display: inline-block;
      text-align: center;
      vertical-align: middle;
      width: 3rem;
    }

    :hover,
    :focus {
      background-color: ${(props) => props.theme.colors.yellow.primary};
    }
  }

  &[aria-selected='true'] {
    button {
      background-color: ${(props) => props.theme.colors.yellow.primary};
    }
  }
`;

// Renders a span for accessibility purposes (not visible)
const StyledHiddenSpan = styled.span`
  clip: rect(1px 1px 1px 1px);
  clip: rect(1px, 1px, 1px, 1px);
  height: 1px;
  overflow: hidden;
  position: absolute;
  width: 1px;
`;

const StyledChevron = styled(ChevronDown)`
  height: 1rem;
  width: 1rem;

  & svg {
    height: 100%;
    width: 100%;
  }

  transform: ${(props) =>
    props['data-isopen'] ? 'rotate(180deg)' : 'rotate(0)'};
  transition: transform 0.2s ease-in;
`;

const StyledMapPinFilled = styled.div`
  height: 1rem;
  width: 1rem;

  & svg {
    height: 100%;
    width: 100%;
  }
`;

/**
 * Renders a region selector component from an object of region arrays
 * where each key is a country. Accepts an onRegionChange function prop
 * that receives the selected region object as an argument.
 */
function RegionSelector({
  currentRegion: currentRegionProp,
  regions,
  onRegionChange,
  onToggleClick,
  controlledIsOpen,
}) {
  const [currentRegion, setCurrentRegion] = useState(currentRegionProp);

  // update currentRegion state when currentRegionProp changes
  useEffect(() => {
    if (currentRegion.id !== currentRegionProp.id) {
      setCurrentRegion(currentRegionProp);
    }
  }, [currentRegionProp]);

  const listRef = createRef();

  /**
   * Checks if the region list pop-up is out of the bounds of
   * the viewport (i.e. has a negative left distance) and applies
   * a right offset to compensate. This is necessary because the
   * element is absolutely positioned relative to the parent toggle
   * button and a CSS-only solution was not found.
   */
  function setListOffset() {
    const listElement = listRef.current;
    const rightStyle = (listElement && listElement.style.right) || '0';
    const leftDistance =
      (listElement && listElement.getBoundingClientRect().left) || 0;
    const rightOffset =
      parseInt(leftDistance, 10) + parseInt(rightStyle.replace('px', ''), 10);

    if (rightOffset < 0) {
      listElement.style.right = `${rightOffset}px`;
    } else {
      listElement.style.right = 0;
    }
  }

  // Hook that performs side effects on mount/update. See: https://reactjs.org/docs/hooks-effect.html
  useEffect(() => {
    // set region list offset in case it would be initially out of bounds
    setListOffset();

    // keep region list within bounds of viewport despite resize/orientation change
    if (window) {
      window.addEventListener('resize', setListOffset);
      window.addEventListener('orientationchange', setListOffset);
    }

    // remove the event listener in the cleanup phase
    return () => {
      if (window) {
        window.removeEventListener('resize', setListOffset);
        window.removeEventListener('orientationchange', setListOffset);
      }
    };
  });

  /**
   * Renders the region options in Downshift. Filters out
   * invalid region objects and pushes the elements to render
   * in the returned array.
   *
   * @param {Function} getItemProps returns Downshift item props
   * @param {Object} selectedItem currently selected item
   * @returns {Array} items to render
   */
  function renderRegions(getItemProps, selectedItem) {
    const itemsToRender = [];

    Object.keys(regions).forEach((country) => {
      // renders a country title before each regions array
      itemsToRender.push(<h4 key={country}>{country}</h4>);

      const sortedRegions = regions[country]
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder);

      sortedRegions.forEach((region) => {
        if (Number.isInteger(region.id) && typeof region.title === 'string') {
          itemsToRender.push(
            <StyledListEntry
              {...getItemProps({
                key: region.id,
                item: region,
                'aria-label': `${region.title}, ${country}. ${
                  selectedItem === region ? 'Currently selected.' : ''
                }`,
              })}
              theme={theme}
            >
              <button
                id={`header-regions-list-${region.title.replace(/ /, '-')}`}
                type="button"
              >
                <span>{selectedItem === region ? <Check /> : null}</span>
                <span>{region.title}</span>
              </button>
            </StyledListEntry>
          );
        } else {
          console.error(
            'Invalid region data in RegionSelect component:',
            region
          );
        }
      });
    });
    return itemsToRender;
  }

  /**
   * Click handler for the region menu toggle button
   *
   * @param {Boolean} isOpen region menu open state after toggle
   */
  function toggleClickHandler(isOpen) {
    return () => {
      if (typeof onToggleClick === 'function') {
        onToggleClick(isOpen);
      } else {
        console.error('onToggleClick prop in RegionSelector is not a function');
      }
    };
  }

  /**
   * Change handler for the region menu
   *
   * @param {Object} item selected menu item
   */
  const onChangeHandler = useCallback((item) => {
    setCurrentRegion(item);

    if (typeof onRegionChange === 'function') {
      onRegionChange(item);
    } else {
      console.error('onRegionChange prop in RegionSelector is not a function');
    }
  });

  return (
    <Downshift
      initialSelectedItem={currentRegionProp}
      selectedItem={currentRegion}
      isOpen={controlledIsOpen}
      onChange={onChangeHandler}
      itemToString={(item) => (item ? `${item.title} region` : '')}
    >
      {({
        getItemProps,
        getLabelProps,
        getMenuProps,
        isOpen,
        getRootProps,
        getToggleButtonProps,
        selectedItem,
        getInputProps,
      }) => (
        <StyledRegionContainer {...getRootProps()}>
          <StyledHiddenSpan id="region-button-description">
            {`Region selector. ${selectedItem.title} region currently selected.`}
          </StyledHiddenSpan>
          <RegionButton
            id="region-selector-dropdown"
            type="button"
            // isOpen is negated to reflect the value it should have after click
            {...getToggleButtonProps({ onClick: toggleClickHandler(!isOpen) })}
            {...getInputProps({
              'aria-labelledby': 'region-button-description',
            })}
          >
            <StyledMapPinFilled>
              <MapPinFilled id="region-selector-map-pin" />
            </StyledMapPinFilled>

            <RegionText id="region-selector-dropdown-text">
              <span
                {...getLabelProps({
                  'aria-label': `${selectedItem.title} region`,
                })}
              >
                {selectedItem.title}
              </span>
            </RegionText>
            <StyledChevron
              id="region-selector-chevron-down"
              data-isopen={isOpen}
            />
          </RegionButton>
          <StyledMenu
            id="region-selector-dropdown-content"
            {...getMenuProps({ isOpen }, { suppressRefError: true })}
            ref={listRef}
            theme={theme}
          >
            {isOpen ? renderRegions(getItemProps, selectedItem) : null}
          </StyledMenu>
        </StyledRegionContainer>
      )}
    </Downshift>
  );
}

/**
 * propTypes for this component
 */
RegionSelector.propTypes = {
  /** The currently active region */
  currentRegion: PropTypes.shape({
    title: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
    sortOrder: PropTypes.number.isRequired,
  }).isRequired,
  /** Region object of arrays */
  regions: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        title: PropTypes.string.isRequired,
        sortOrder: PropTypes.number.isRequired,
      })
    )
  ).isRequired,
  /** Function that receives selected region object on change */
  onRegionChange: PropTypes.func,
  /** Function that receives menu open state on toggle button click */
  onToggleClick: PropTypes.func,
  /** Dropdown open state */
  controlledIsOpen: PropTypes.bool,
};

/**
 * Default prop types
 */
RegionSelector.defaultProps = {
  onRegionChange: () => {},
  onToggleClick: () => {},
  controlledIsOpen: undefined,
};

/* @component */
export default RegionSelector;
